'use client';
import * as React from 'react';
import PropTypes from 'prop-types';
import Link from '@mui/material/Link';
import { styled } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useSlotProps from '@mui/utils/useSlotProps';
import Breadcrumbs from '@mui/material/Breadcrumbs';

import { useActivePage } from '../useActivePage';
import { getItemTitle } from '../shared/navigation';
import { Link as ToolpadLink } from '../shared/Link';

import { Breadcrumb } from './PageContainer';
import { PageHeaderToolbar, PageHeaderToolbarProps } from './PageHeaderToolbar';

const PageContentHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
}));

export interface PageHeaderSlotProps {
  toolbar: PageHeaderToolbarProps;
}

export interface PageHeaderSlots {
  /**
   * The component that renders the actions toolbar.
   * @default PageHeaderToolbar
   */
  toolbar: React.ElementType;
}

export interface PageHeaderProps {
  /**
   * The title of the page. Leave blank to use the active page title.
   */
  title?: string;
  /**
   * The components used for each slot inside.
   */
  slots?: PageHeaderSlots;
  /**
   * The breadcrumbs of the page. Leave blank to use the active page breadcrumbs.
   */
  breadcrumbs?: Breadcrumb[];
  /**
   * The props used for each slot inside.
   */
  slotProps?: PageHeaderSlotProps;
}

/**
 * A header component to provide a title and breadcrumbs for your pages.
 *
 * Demos:
 *
 * - [Page Container](https://mui.com/toolpad/core/react-page-container/)
 *
 * API:
 *
 * - [PageHeader API](https://mui.com/toolpad/core/api/page-header)
 */
function PageHeader(props: PageHeaderProps) {
  const { title, breadcrumbs } = props;

  const activePage = useActivePage();

  const resolvedBreadcrumbs = breadcrumbs ?? activePage?.breadcrumbs ?? [];
  const resolvedTitle = title ?? activePage?.title ?? '';

  const ToolbarComponent = props?.slots?.toolbar ?? PageHeaderToolbar;
  const toolbarSlotProps = useSlotProps({
    ownerState: props,
    additionalProps: {},
    elementType: ToolbarComponent,
    externalSlotProps: props?.slotProps?.toolbar,
  });

  return (
    <Stack>
      <Breadcrumbs aria-label="breadcrumb">
        {resolvedBreadcrumbs
          ? resolvedBreadcrumbs.map((item, index) => {
              return item.path ? (
                <Link
                  key={index}
                  color="inherit"
                  href={item.path}
                  underline="hover"
                  component={ToolpadLink}
                >
                  {getItemTitle(item)}
                </Link>
              ) : (
                <Typography
                  key={index}
                  sx={{
                    color: 'text.primary',
                  }}
                >
                  {getItemTitle(item)}
                </Typography>
              );
            })
          : null}
      </Breadcrumbs>
      <PageContentHeader>
        {resolvedTitle ? (
          <Typography variant="h4">{resolvedTitle}</Typography>
        ) : null}
        <ToolbarComponent {...toolbarSlotProps} />
      </PageContentHeader>
    </Stack>
  );
}

PageHeader.propTypes /* remove-proptypes */ = {
  /**
   * The title of the page. Leave blank to use the active page title.
   */
  title: PropTypes.string,
  /**
   * The components used for each slot inside.
   */
  slots: PropTypes.shape({
    toolbar: PropTypes.elementType,
  }),
  /**
   * The props used for each slot inside.
   */
  slotProps: PropTypes.shape({
    toolbar: PropTypes.shape({
      children: PropTypes.node,
    }).isRequired,
  }),
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * The breadcrumbs of the page. Leave blank to use the active page breadcrumbs.
   */
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      title: PropTypes.string.isRequired,
    })
  ),
} as any;

export { PageHeader };
